import { useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useParcelIssue } from '../../hooks/useParcelIssue'
import { useUsers } from '../../hooks/useUsers'
import { useAuth } from '../../contexts/AuthContext'
import type { IssueStatusLog, IssueMessage, IssueAttachment } from '../../hooks/useParcelIssue'

const STATUS_COLORS: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  investigating: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  awaiting_carrier: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  closed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
}

const TYPE_COLORS: Record<string, string> = {
  lost: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  damaged: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  delayed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  missing_scan: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  wrong_address: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
}

const STATUSES = ['submitted', 'investigating', 'awaiting_carrier', 'resolved', 'closed']

function fmtLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function fmtDate(d: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtDateTime(d: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</span>
      <span className="text-sm text-gray-900 dark:text-white text-right max-w-[60%]">{value || '—'}</span>
    </div>
  )
}

export default function IssueDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, userEmail } = useAuth()
  const { users } = useUsers()

  const { issue, messages, attachments, statusLog, loading, error,
    updateIssue, changeStatus, addMessage, uploadFile } = useParcelIssue(id)

  // Action bar state
  const [newStatus, setNewStatus] = useState('')
  const [statusReason, setStatusReason] = useState('')
  const [changingStatus, setChangingStatus] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)

  const [assignTo, setAssignTo] = useState('')
  const [assigning, setAssigning] = useState(false)

  const [msgBody, setMsgBody] = useState('')
  const [isInternal, setIsInternal] = useState(true)
  const [sendingMsg, setSendingMsg] = useState(false)

  // Outcome/payout edit
  const [editingOutcome, setEditingOutcome] = useState(false)
  const [outcome, setOutcome] = useState<string>('')
  const [payoutAmount, setPayoutAmount] = useState('')
  const [payoutYear, setPayoutYear] = useState('')
  const [savingOutcome, setSavingOutcome] = useState(false)

  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white'

  async function handleStatusChange() {
    if (!newStatus) return
    setChangingStatus(true)
    setStatusError(null)
    try {
      await changeStatus(newStatus, user?.id ?? null, statusReason || null)
      setNewStatus('')
      setStatusReason('')
    } catch (e: any) {
      setStatusError(e.message)
    } finally {
      setChangingStatus(false)
    }
  }

  async function handleAssign() {
    if (!assignTo) return
    setAssigning(true)
    try {
      await updateIssue({ assigned_to: assignTo as any })
      setAssignTo('')
    } finally {
      setAssigning(false)
    }
  }

  async function handleSendMessage(e: React.FormEvent) {
    e.preventDefault()
    if (!msgBody.trim()) return
    setSendingMsg(true)
    try {
      await addMessage(msgBody, user?.id ?? null, userEmail, 'staff', isInternal)
      setMsgBody('')
    } finally {
      setSendingMsg(false)
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await uploadFile(file, user?.id ?? null)
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function handleSaveOutcome() {
    setSavingOutcome(true)
    try {
      await updateIssue({
        outcome: (outcome as any) || null,
        payout_amount: payoutAmount ? parseFloat(payoutAmount) : null,
        payout_year: payoutYear ? parseInt(payoutYear) : null,
      })
      setEditingOutcome(false)
    } finally {
      setSavingOutcome(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">Loading…</p>
      </div>
    )
  }

  if (error || !issue) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
        <p className="text-red-600 dark:text-red-400">{error || 'Issue not found'}</p>
        <button onClick={() => navigate('/parcel-issues')} className="mt-4 text-sm text-blue-600 underline">
          Back to list
        </button>
      </div>
    )
  }

  const assignedUser = users.find(u => u.id === issue.assigned_to)
  const staffUsers = users.filter(u => u.is_active && !(u.roles?.length ? u.roles : [u.role]).includes('CLIENT'))

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/parcel-issues')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-red-700 dark:text-red-400 font-mono">{issue.issue_no}</h1>
              {issue.issue_type && (
                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${TYPE_COLORS[issue.issue_type] || ''}`}>
                  {fmtLabel(issue.issue_type)}
                </span>
              )}
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[issue.status] || ''}`}>
                {fmtLabel(issue.status)}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {issue.agent_name || 'No agent'} {issue.importer_name ? `· ${issue.importer_name}` : ''}
            </p>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* LEFT: Parcel info + Timeline + Outcome */}
          <div className="lg:col-span-3 space-y-4">
            {/* Parcel Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Parcel Information</h2>
              </div>
              <div className="px-5 py-3">
                <div className="grid grid-cols-2 gap-x-8">
                  <div>
                    <InfoRow label="Waybill" value={<span className="font-mono">{issue.waybill_number}</span>} />
                    <InfoRow label="Tracking" value={<span className="font-mono">{issue.tracking_number}</span>} />
                    <InfoRow label="CYUK Job Ref" value={<span className="font-mono">{issue.cyuk_job_ref}</span>} />
                    <InfoRow label="Carrier" value={issue.carrier} />
                    <InfoRow label="Warehouse" value={issue.warehouse} />
                    <InfoRow label="Ship Date" value={fmtDate(issue.ship_date)} />
                  </div>
                  <div>
                    <InfoRow label="Origin" value={issue.origin_country} />
                    <InfoRow label="Destination" value={issue.destination_country} />
                    <InfoRow label="Cartons" value={issue.cartons?.toString()} />
                    <InfoRow label="Weight" value={issue.weight_kg ? `${issue.weight_kg} kg` : null} />
                    <InfoRow label="Declared Value" value={issue.declared_value ? `${issue.currency} ${issue.declared_value.toFixed(2)}` : null} />
                    <InfoRow label="Assigned To" value={assignedUser?.full_name || assignedUser?.user_email || issue.assigned_to} />
                  </div>
                </div>
                {issue.notes && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">Notes</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{issue.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Timeline</h2>
              </div>
              <div className="px-5 py-4">
                <div className="space-y-3">
                  {/* Created */}
                  <TimelineItem
                    icon="circle"
                    color="blue"
                    text={`Issue logged`}
                    sub={fmtDateTime(issue.created_at)}
                  />
                  {statusLog.map((log: IssueStatusLog) => (
                    <TimelineItem
                      key={log.id}
                      icon="arrow"
                      color="amber"
                      text={`Status: ${log.from_status ? fmtLabel(log.from_status) + ' → ' : ''}${fmtLabel(log.to_status)}`}
                      sub={`${fmtDateTime(log.created_at)}${log.reason ? ` · ${log.reason}` : ''}`}
                    />
                  ))}
                  {issue.resolved_at && (
                    <TimelineItem
                      icon="check"
                      color="green"
                      text="Issue resolved"
                      sub={fmtDateTime(issue.resolved_at)}
                    />
                  )}
                </div>
                {statusLog.length === 0 && !issue.resolved_at && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">No status changes yet.</p>
                )}
              </div>
            </div>

            {/* Outcome & Payout */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Outcome & Payout</h2>
                {!editingOutcome && (
                  <button
                    onClick={() => {
                      setOutcome(issue.outcome || '')
                      setPayoutAmount(issue.payout_amount?.toString() || '')
                      setPayoutYear(issue.payout_year?.toString() || '')
                      setEditingOutcome(true)
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="px-5 py-4">
                {editingOutcome ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Outcome</label>
                        <select value={outcome} onChange={e => setOutcome(e.target.value)} className={inputCls}>
                          <option value="">— Pending —</option>
                          <option value="accepted">Accepted</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Payout Amount (GBP)</label>
                        <input type="number" min="0" step="0.01" value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)} className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Payout Year</label>
                        <input type="number" min="2020" max="2099" value={payoutYear} onChange={e => setPayoutYear(e.target.value)} className={inputCls} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleSaveOutcome} disabled={savingOutcome}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md disabled:opacity-50">
                        {savingOutcome ? 'Saving…' : 'Save'}
                      </button>
                      <button onClick={() => setEditingOutcome(false)}
                        className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <InfoRow label="Outcome" value={
                      issue.outcome ? (
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${issue.outcome === 'accepted' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                          {fmtLabel(issue.outcome)}
                        </span>
                      ) : <span className="text-gray-400 text-xs">Pending</span>
                    } />
                    <InfoRow label="Payout Amount" value={issue.payout_amount ? `£${issue.payout_amount.toFixed(2)}` : null} />
                    <InfoRow label="Payout Year" value={issue.payout_year?.toString()} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Messages + Attachments + Action Bar */}
          <div className="lg:col-span-2 space-y-4">
            {/* Action Bar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</h2>
              </div>
              <div className="px-5 py-4 space-y-4">
                {/* Status change */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Change Status</label>
                  <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className={inputCls}>
                    <option value="">Current: {fmtLabel(issue.status)}</option>
                    {STATUSES.filter(s => s !== issue.status).map(s => (
                      <option key={s} value={s}>{fmtLabel(s)}</option>
                    ))}
                  </select>
                  {newStatus && (
                    <div className="mt-2 space-y-2">
                      <input
                        type="text"
                        value={statusReason}
                        onChange={e => setStatusReason(e.target.value)}
                        placeholder="Reason (optional)"
                        className={inputCls}
                      />
                      {statusError && (
                        <p className="text-xs text-red-600 dark:text-red-400">{statusError}</p>
                      )}
                      <button
                        onClick={handleStatusChange}
                        disabled={changingStatus}
                        className="w-full py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-md disabled:opacity-50"
                      >
                        {changingStatus ? 'Updating…' : `Set to ${fmtLabel(newStatus)}`}
                      </button>
                    </div>
                  )}
                </div>

                {/* Assign */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Assign To</label>
                  <div className="flex gap-2">
                    <select value={assignTo} onChange={e => setAssignTo(e.target.value)} className={inputCls}>
                      <option value="">{assignedUser?.full_name || assignedUser?.user_email || '— Unassigned —'}</option>
                      {staffUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.full_name || u.user_email}</option>
                      ))}
                    </select>
                    <button
                      onClick={handleAssign}
                      disabled={!assignTo || assigning}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md disabled:opacity-50 whitespace-nowrap"
                    >
                      {assigning ? '…' : 'Assign'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Messages {messages.length > 0 && <span className="text-gray-400 font-normal">({messages.length})</span>}
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-72 overflow-y-auto">
                {messages.length === 0 ? (
                  <p className="px-5 py-4 text-xs text-gray-400 dark:text-gray-500">No messages yet.</p>
                ) : messages.map((msg: IssueMessage) => (
                  <div key={msg.id} className={`px-5 py-3 ${msg.is_internal ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{msg.author_name || msg.author_role}</span>
                      <div className="flex items-center gap-2">
                        {msg.is_internal && (
                          <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-1.5 py-0.5 rounded">Internal</span>
                        )}
                        <span className="text-xs text-gray-400 dark:text-gray-500">{fmtDateTime(msg.created_at)}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{msg.body}</p>
                  </div>
                ))}
              </div>
              {/* New message */}
              <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                <form onSubmit={handleSendMessage} className="space-y-2">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Mode:</span>
                    <button
                      type="button"
                      onClick={() => setIsInternal(true)}
                      className={`text-xs px-2 py-1 rounded ${isInternal ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                      Internal Note
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsInternal(false)}
                      className={`text-xs px-2 py-1 rounded ${!isInternal ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                      Customer Message
                    </button>
                  </div>
                  <textarea
                    value={msgBody}
                    onChange={e => setMsgBody(e.target.value)}
                    rows={3}
                    placeholder={isInternal ? 'Internal note (not visible to customer)…' : 'Message to customer…'}
                    className={inputCls}
                  />
                  <button
                    type="submit"
                    disabled={sendingMsg || !msgBody.trim()}
                    className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md disabled:opacity-50"
                  >
                    {sendingMsg ? 'Sending…' : isInternal ? 'Add Note' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Attachments {attachments.length > 0 && <span className="text-gray-400 font-normal">({attachments.length})</span>}
                </h2>
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 disabled:opacity-50"
                  >
                    {uploading ? 'Uploading…' : '+ Upload'}
                  </button>
                </div>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {attachments.length === 0 ? (
                  <p className="px-5 py-4 text-xs text-gray-400 dark:text-gray-500">No attachments.</p>
                ) : attachments.map((att: IssueAttachment) => (
                  <div key={att.id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-800 dark:text-gray-200 font-medium truncate max-w-[200px]">{att.file_name}</p>
                      {att.description && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">{att.description}</p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {att.file_size ? `${(att.file_size / 1024).toFixed(0)} KB · ` : ''}
                        {fmtDate(att.created_at)}
                      </p>
                    </div>
                    {att.public_url && (
                      <a
                        href={att.public_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 ml-3 flex-shrink-0"
                      >
                        View
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function TimelineItem({
  color, text, sub,
}: {
  icon: 'circle' | 'arrow' | 'check'
  color: 'blue' | 'amber' | 'green'
  text: string
  sub: string
}) {
  const dotColor = {
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    green: 'bg-green-500',
  }[color]

  return (
    <div className="flex items-start gap-3">
      <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${dotColor}`} />
      <div>
        <p className="text-sm text-gray-800 dark:text-gray-200">{text}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p>
      </div>
    </div>
  )
}
